import { useId, useState, type FormEvent } from 'react'

import type { Competitor } from '@/api/market'

import { useAddTrackedCompetition, useFormFields } from './hooks'

const EMPTY = {
  competitorId: '',
  prize: '',
  category: '',
  ticketPrice: '',
  maxEntries: '',
  instantWins: '0',
  startDate: '',
  endDate: '',
  promotionalMessaging: '',
}

/** A manual-entry form for one tracked competition, per the acceptance criteria's TrackedCompetition model. */
export function AddTrackedCompetitionForm({ competitors, onDone }: { competitors: readonly Competitor[]; onDone: () => void }) {
  const formId = useId()
  const { values, update, reset } = useFormFields({ ...EMPTY, competitorId: competitors[0]?.id ?? '' })
  const addTrackedCompetition = useAddTrackedCompetition()
  const [dateError, setDateError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!values.competitorId) return
    if (values.endDate < values.startDate) {
      setDateError('The end date must be on or after the start date.')
      return
    }
    setDateError(null)
    try {
      await addTrackedCompetition.mutateAsync({
        competitorId: values.competitorId,
        prize: values.prize.trim(),
        category: values.category.trim(),
        ticketPrice: Number(values.ticketPrice) || 0,
        maxEntries: Math.round(Number(values.maxEntries) || 0),
        instantWins: Math.round(Number(values.instantWins) || 0),
        startDate: values.startDate,
        endDate: values.endDate,
        promotionalMessaging: values.promotionalMessaging.trim(),
      })
      reset({ ...EMPTY, competitorId: values.competitorId })
      onDone()
    } catch {
      // addTrackedCompetition.isError drives the message below; the form stays open so the viewer can retry.
    }
  }

  if (competitors.length === 0) {
    return <p className="mk-form__empty">Add a competitor first, then track one of their competitions.</p>
  }

  return (
    <form className="mk-form" aria-label="Add a tracked competition" onSubmit={(event) => void handleSubmit(event)}>
      <div className="mk-form__grid">
        <label htmlFor={`${formId}-competitor`}>
          Competitor
          <select id={`${formId}-competitor`} value={values.competitorId} onChange={(event) => update('competitorId', event.target.value)}>
            {competitors.map((competitor) => (
              <option key={competitor.id} value={competitor.id}>
                {competitor.name}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={`${formId}-prize`}>
          Prize
          <input id={`${formId}-prize`} required value={values.prize} onChange={(event) => update('prize', event.target.value)} />
        </label>
        <label htmlFor={`${formId}-category`}>
          Category
          <input id={`${formId}-category`} required value={values.category} onChange={(event) => update('category', event.target.value)} />
        </label>
        <label htmlFor={`${formId}-price`}>
          Ticket price
          <input
            id={`${formId}-price`}
            type="number"
            min="0"
            step="0.01"
            required
            value={values.ticketPrice}
            onChange={(event) => update('ticketPrice', event.target.value)}
          />
        </label>
        <label htmlFor={`${formId}-max`}>
          Max entries
          <input
            id={`${formId}-max`}
            type="number"
            min="1"
            required
            value={values.maxEntries}
            onChange={(event) => update('maxEntries', event.target.value)}
          />
        </label>
        <label htmlFor={`${formId}-instant`}>
          Instant wins
          <input
            id={`${formId}-instant`}
            type="number"
            min="0"
            value={values.instantWins}
            onChange={(event) => update('instantWins', event.target.value)}
          />
        </label>
        <label htmlFor={`${formId}-start`}>
          Start date
          <input id={`${formId}-start`} type="date" required value={values.startDate} onChange={(event) => update('startDate', event.target.value)} />
        </label>
        <label htmlFor={`${formId}-end`}>
          End date
          <input id={`${formId}-end`} type="date" required value={values.endDate} onChange={(event) => update('endDate', event.target.value)} />
        </label>
        <label htmlFor={`${formId}-promo`} className="mk-form__wide">
          Promotional messaging
          <input
            id={`${formId}-promo`}
            value={values.promotionalMessaging}
            onChange={(event) => update('promotionalMessaging', event.target.value)}
          />
        </label>
        {dateError ? (
          <p className="mk-form__failure" role="alert">
            {dateError}
          </p>
        ) : null}
        {addTrackedCompetition.isError ? (
          <p className="mk-form__failure" role="alert">
            Could not add this tracked competition. Try again.
          </p>
        ) : null}
      </div>
      <div className="mk-form__actions">
        <button type="submit" className="ci-button" data-variant="primary" disabled={addTrackedCompetition.isPending}>
          Add tracked competition
        </button>
        <button type="button" className="ci-button" data-variant="ghost" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  )
}
